<script setup lang="ts">
import { computed } from 'vue'
import JBreadcrumb from '@/components/molecules/JBreadcrumb.vue'
import JTitlebar from '@/components/molecules/JTitlebar.vue'
import { cn } from '@/lib/utils'
import type { BreadcrumbItem } from '@/components/molecules/JBreadcrumb.vue'
import type { TitlebarButton } from '@/components/molecules/JTitlebar.vue'

/**
 * JPageContainer - 기본 페이지 컨테이너 컴포넌트 (organisms)
 * Page Container Component
 * 
 * @description
 * 페이지의 기본 레이아웃을 담당하는 컨테이너 컴포넌트입니다.
 * 브레드크럼, 제목 영역(JTitlebar), 콘텐츠 영역을 포함합니다.
 * 
 * @example
 * ```vue
 * <JPageContainer
 *   :breadcrumb-items="breadcrumbItems"
 *   title="페이지 제목"
 *   :titlebar-buttons="buttons"
 * >
 *   <div>페이지 콘텐츠</div>
 * </JPageContainer>
 * ```
 */

type StyleType =
  | 'default'   // 기본 스타일
  | 'minimal'   // 최소 스타일

const props = withDefaults(
  defineProps<{
    /** 브레드크럼 아이템 목록 */
    breadcrumbItems?: BreadcrumbItem[]
    /** 브레드크럼 표시 여부 */
    showBreadcrumb?: boolean
    /** JTitlebar의 모든 props 전달 */
    title?: string
    icon?: string
    description?: string
    titlebarButtons?: TitlebarButton[]
    titlebarStyletype?: 'default' | 'primary' | 'accent' | 'neutral' | 'elevated'
    /** JTitlebar 표시 여부 */
    showTitlebar?: boolean
    /** 스타일 타입 */
    styletype?: StyleType
    /** 콘텐츠 영역 스크롤 가능 여부 */
    contentScroll?: boolean
    /** 추가 CSS 클래스 */
    class?: string
  }>(),
  {
    breadcrumbItems: () => [],
    showBreadcrumb: true,
    showTitlebar: true,
    styletype: 'default',
    contentScroll: true,
  }
)

const emit = defineEmits<{
  /** 브레드크럼 아이템 클릭 이벤트 */
  breadcrumbClick: [item: BreadcrumbItem, index: number]
  /** 타이틀바 버튼 클릭 이벤트 */
  titlebarButtonClick: [button: TitlebarButton]
}>()

/**
 * 스타일 프리셋
 */
const STYLE_PRESETS: Record<StyleType, {
  containerClass: string
  contentClass: string
}> = {
  default: {
    containerClass: 'flex flex-col h-full w-full bg-background',
    contentClass: 'flex-1 flex flex-col p-1.5 md:p-2 gap-2 bg-background text-foreground min-h-0',
  },
  minimal: {
    containerClass: 'flex flex-col h-full w-full bg-background',
    contentClass: 'flex-1 flex flex-col p-1 md:p-1.5 gap-1 bg-background text-foreground min-h-0',
  },
}

const preset = computed(() => {
  return STYLE_PRESETS[props.styletype] ?? STYLE_PRESETS.default
})

/**
 * 콘텐츠 클래스 (스크롤 포함)
 */
const contentClasses = computed(() => {
  return cn(
    preset.value.contentClass,
    props.contentScroll ? 'overflow-auto' : 'overflow-hidden'
  )
})

/**
 * 브레드크럼 아이템 클릭 핸들러
 */
const handleBreadcrumbClick = (item: BreadcrumbItem, index: number) => {
  emit('breadcrumbClick', item, index)
}

/**
 * 타이틀바 버튼 클릭 핸들러
 */
const handleTitlebarButtonClick = (button: TitlebarButton) => {
  emit('titlebarButtonClick', button)
}
</script>

<template>
  <div :class="cn(preset.containerClass, props.class)">
    <!-- 브레드크럼 -->
    <JBreadcrumb
      v-if="showBreadcrumb && breadcrumbItems && breadcrumbItems.length > 0"
      :items="breadcrumbItems"
      @item-click="handleBreadcrumbClick"
    />

    <!-- 제목 영역 (JTitlebar) -->
    <JTitlebar
      v-if="showTitlebar"
      :title="title"
      :icon="icon"
      :description="description"
      :buttons="titlebarButtons"
      :styletype="titlebarStyletype"
      @button-click="handleTitlebarButtonClick"
    >
      <template #buttons>
        <slot name="titlebar-buttons" />
      </template>
    </JTitlebar>

    <!-- 콘텐츠 영역 -->
    <div :class="contentClasses">
      <slot />
    </div>
  </div>
</template>
